/**
 * Copyright (c) 2022 Strategio Digital s.r.o.
 * @author Jiří Zapletal (https://strategio.digital, jz@strategio.digital)
 */

import { TInvoice, TInvoiceHydrated } from '@/types/TInvoice'
import { invoiceHelper } from '@/helpers/invoiceHelper'
import { timeHelper } from '@/helpers/timeHelper'
import { TInvoiceItemHydrated, TInvoiceItem } from '@/types/TInvoiceItem'
import { priceHelper } from '@/helpers/priceHelper'

const { makePrettyNumber } = invoiceHelper()
const { toCzechDateString, toCzechDateTimeString, pbDateToObject } = timeHelper()
const { toFormattedPrice } = priceHelper()

const hydrateInvoice = (invoice: TInvoice): TInvoiceHydrated => {
    const issueDate = pbDateToObject(invoice.issueDate)
    const dueDate = pbDateToObject(invoice.dueDate)

    const canceledDate = invoice.canceledDate ? pbDateToObject(invoice.canceledDate) : undefined
    const paidDate = invoice.paidDate ? pbDateToObject(invoice.paidDate) : undefined

    return {
        ...invoice,
        canceledDate: canceledDate ? toCzechDateTimeString(canceledDate) : undefined,
        paidDate: paidDate ? toCzechDateTimeString(paidDate) : undefined,
        issueDate: toCzechDateString(issueDate),
        dueDate: toCzechDateString(dueDate),
        invoiceNumberPretty: makePrettyNumber(invoice.invoiceNumber),
        invoiceTypeShort: invoice.proforma ? 'PR' : 'FA',
        variableSymbol: invoice.invoiceNumber,
        sumPriceFormatted: toFormattedPrice(invoice.sumPrice, invoice.currency)
    }
}

const hydrateInvoiceItem = (item: TInvoiceItem, currency: string): TInvoiceItemHydrated => {
    const price = item.price || 0
    const sumPrice = price * (item.quantity || 0)

    return {
        ...item,
        sumPrice,
        priceFormatted: toFormattedPrice(price, currency),
        sumPriceFormatted: toFormattedPrice(sumPrice, currency)
    }
}

export const invoiceHydrator = () => {
    return {
        hydrateInvoiceItem,
        hydrateInvoice
    }
}